import BaseForgeCustomizer from '../base-forge';
import { userPasswordResetInit, submitLegacyPasswordResetForm, isRetryableError, getTransformedError } from '../../utils/password-reset-api';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { getOktaWidgetModel, startSpinner, stopSpinner } from '../../utils/widget-facade';
import { ERROR_KEYS, ERROR_MESSAGES, ANET_USER_PREFIX_SUPPORT_ENV_MAP, NON_FUZZY_ANET_USER_DOMAIN, FUZZY_ENV_MAPPING } from '../../constants';
import InfoContainer from '../../components/common/InfoContainer';
import ForgotUsernameLink from '../../components/password-reset/ForgotUsernameLink';
import ForgotUsernamePage from '../../components/password-reset/ForgotUsernamePage';
import { createWrapper } from '../../utils/react-utils';
import BackToLogin from '../../components/common/BackToLogin';
import CommonFooter from '../../components/common/Footer';
import { indefiniteWaitPromise } from '../../utils/common-utils';
import BlockProdUserPasswordResetInSupportBanner from '../../components/password-reset/BlockPasswordResetForProdUserInSupport';
import SSOUserResetPasswordBanner from '../../components/password-reset/SSOUserResetPasswordBanner';
import { getAthenaOneHost } from '../../utils/app-utils';
import { prefetchAdminCoverage } from '../../utils/admin-coverage-utils';
import { setUserStartLoginWorkflowCookie } from '../../utils/storage-utils';

/**
 * Validates if the input is a valid athenanet username and extracts the username part
 *
 * NOTE: The Okta Sign-In Widget's transformUsername function automatically appends the anet domain
 * to plain usernames before calling idx.proceed, so input is always in email format:
 * - 'john123@managedidentity.platform.athenahealth.com' (production)
 * - 'p-czhang14@preview.managedidentity.platform.athenahealth.com' (support env)
 *
 * NOTE: Input is expected to already be lowercase (handled by caller)
 *
 * Valid username format (before @ symbol):
 * - Must start with lowercase letter, followed by lowercase letters/numbers only
 * - Can have optional prefix (p-, s-, d-, c-, cs-) for support environments
 *
 * Valid domain:
 * - NON_FUZZY_ANET_USER_DOMAIN or one of the FUZZY_ENV_MAPPING domains
 *
 * Returns the username part if valid, or null if invalid
 * This rejects:
 * - Personal emails (user@gmail.com)
 * - Passwords with special characters
 * - Invalid formats
 */
function validateAndExtractUsername(input: string): string | null {
  const [ username, domain ] = input.split('@');

  // Check if domain is a valid anetUserDomain
  const validDomains = [
    NON_FUZZY_ANET_USER_DOMAIN,
    ...Object.values(FUZZY_ENV_MAPPING).map((env: any) => env.anetUserDomain),
  ];

  if (!validDomains.includes(domain)) {
    // Invalid domain - likely a personal email
    return null;
  }

  // Validate username format
  // Build regex pattern from supported prefixes in ANET_USER_PREFIX_SUPPORT_ENV_MAP
  // Sort by length descending to match longer prefixes first (e.g., 'cs' before 'c')
  const prefixes = Object.keys(ANET_USER_PREFIX_SUPPORT_ENV_MAP)
    .sort((a, b) => b.length - a.length)
    .join('|');
  const pattern = new RegExp(`^((${prefixes})-)?[a-z][a-z0-9]*$`);

  // Check if username matches: optional (prefix + dash) OR just lowercase letter followed by alphanumeric
  if (!pattern.test(username)) {
    return null;
  }

  return username;
}

/**
 * Customizer for forgot password flow
 * Intercepts idx.proceed to check eligibility before OSW processes the request
 * Also handles forgot username flow
 */
export default class PasswordResetInitCustomizer extends BaseForgeCustomizer {
  async shouldCustomize() {
    return this.context?.controller === 'forgot-password' &&
    this.context?.formName === 'identify-recovery';
  }

  protected blockPasswordUpdateForProdUser() {
    const wrapper = createWrapper({ hidden: false });
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    formContainer.append(wrapper);
    this.renderReactComponent({
      id: 'block-prod-user-in-support',
      container: wrapper,
      component: <BlockProdUserPasswordResetInSupportBanner />,
    });
  }

  protected renderSSOUserBanner(
    identityProviders: Record<string, { contexts: Record<string, string>; nameid: string }>,
    athenaHost: string
  ) {
    const wrapper = createWrapper({ hidden: false });
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    formContainer.append(wrapper);
    this.renderReactComponent({
      id: 'sso-user-reset-password-banner',
      container: wrapper,
      component: <SSOUserResetPasswordBanner identityProviders={identityProviders} athenaHost={athenaHost} />,
    });
  }

  async customize() {
    // Check sessionStorage to see if we should show forgot username page
    const showForgotUsername = sessionStorage.getItem('showForgotUsernamePage') === 'true';
    const showSuccessBanner = sessionStorage.getItem('showForgotUsernameSuccessBanner') === 'true';

    if (showForgotUsername) {
      // Show forgot username page instead of password reset form
      await this.renderForgotUsernamePageOnly();
      // Clear the flag after rendering so it doesn't persist if user navigates away
      sessionStorage.removeItem('showForgotUsernamePage');
    }
    else {
      // Normal password reset flow
      await super.customize();
      this.interceptPasswordReset();

      // Show success banner if returning from forgot username flow
      if (showSuccessBanner) {
        this.showSuccessBanner();
        // Clear the flag from sessionStorage after showing banner
        sessionStorage.removeItem('showForgotUsernameSuccessBanner');
      }
    }
  }

  /**
   * Intercept OSW's idx.proceed to check eligibility before proceeding
   */
  protected interceptPasswordReset() {
    const {
      oktaSignIn: { authClient },
    } = this;

    const originalProceed = authClient.idx.proceed.bind(authClient.idx);

    // Widget calls this method after performing frontend validation
    // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L284
    authClient.idx.proceed = async (...args: any) => {
      // Check if this is the identify step (username entry)
      if (args[0]?.identifier) {
        // Convert input to lowercase to handle case-insensitive usernames
        const inputValue = args[0].identifier.toLowerCase();

        // Validate and extract username from input
        const username = validateAndExtractUsername(inputValue);
        setUserStartLoginWorkflowCookie(args[0]?.identifier);
        const model = getOktaWidgetModel();

        // If validation failed, show error message
        if (!username) {
          model.trigger('error', model, {
            responseJSON: {
              errorSummary: ERROR_MESSAGES[ERROR_KEYS.INVALID_USERNAME_FORMAT],
            },
          });

          // Return a promise that never resolves to prevent form submission
          return indefiniteWaitPromise();
        }

        // This captures the "normalized" error payload from the widget
        // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L385
        model.once('error', (_: any, error: any) => {
          const errorMessage =
            error?.responseJSON?.errorCauses?.[0]?.errorSummary ??
            error?.responseJSON?.errorSummary ??
            error?.message ??
            ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];

          AmplitudeUtils.logCustomEvent('PasswordReset::InitFailed', {
            errorMessage,
            errorCode: error?.errorCode,
            username,
          });
        });

        // Log analytics
        AmplitudeUtils.logCustomEvent('PasswordReset::UsernameSubmitted', {
          username,
        });

        // Call Lambda for eligibility check AND group assignment
        // IMPORTANT: This API call does THREE things:
        // 1. Checks if user is eligible for new password reset flow
        // 2. Checks EPCS status by calling Monolith internally
        // 3. Assigns user to appropriate Okta group:
        //    - Regular users → "ResetPassword athenaOne Users" (MFA required)
        //    - EPCS users → "ResetPassword EPCS athenaOne Users" (No MFA)

        // ALPHA TESTING SAFETY: If this fails, redirect to legacy workflow
        let response;
        try {
          startSpinner({ forceDisplaySpinner: true });
          response = await userPasswordResetInit({
            username: inputValue,
            supportEnv: settings.supportEnv || '',
          });

          // Log result
          AmplitudeUtils.logCustomEvent('PasswordReset::InitCompleted', {
            username,
            eligible: response.eligible,
            useLegacyWorkflow: response.useLegacyWorkflow,
          });
        }
        catch (error) {
          stopSpinner();
          if (isRetryableError(error)) {
            // ALPHA TESTING SAFETY: Fallback to legacy workflow on any error
            AmplitudeUtils.logCustomEvent('PasswordReset::InitFailedFallbackToLegacy', {
              username,
              error: (error as any)?.message || 'Unknown error',
            });
            return submitLegacyPasswordResetForm(username);
          }
          // Throw error to show message on the error banner
          throw getTransformedError(error);
        }

        if (!response.eligible) {
          stopSpinner();
          // User is not eligible - show warning message and hide form
          const warningMessage = response.message || ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];

          // Hide the input field container and button bar
          setTimeout(() => {
            const fieldsetContainer = document.querySelector('[data-se="o-form-fieldset-container"]');
            const buttonBar = document.getElementById('athena-o-form-button-bar');

            if (fieldsetContainer) {
              fieldsetContainer.classList.add('hide-element');
            }

            if (buttonBar) {
              buttonBar.classList.add('hide-element');
            }
          }, 0);

          if (response?.errorCode === 'password.reset.environment.error') {
            this.blockPasswordUpdateForProdUser();
          }
          else if (response.identityproviders && Object.keys(response.identityproviders).length > 0) {
            // User is SSO-only - show SSO banner with login links
            await prefetchAdminCoverage(username);
            const athenaHost = getAthenaOneHost(this.settings.clientId);
            this.renderSSOUserBanner(response.identityproviders, athenaHost);
          }
          else {
            // Show warning banner using event emitter
            this.eventEmitter.emitEventToComponent({
              componentName: InfoContainer.displayName as string,
              eventName: 'set-info-banner',
              eventPayload: {
                headerText: '',
                description: warningMessage,
                alertType: 'attention',
              },
            });
          }


          // Return a Promise that never resolves to prevent form submission
          // This stops the widget from processing further without triggering an error
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return indefiniteWaitPromise();
        }

        if (response.eligible && response.useLegacyWorkflow) {
          stopSpinner();
          // User is eligible but should use legacy workflow
          return submitLegacyPasswordResetForm(username);
        }

        // User is eligible and should use new workflow - continue OSW flow
        const result = originalProceed.apply(authClient.idx, args);
        stopSpinner();
        return result;
      }

      // For other steps, just proceed normally
      return originalProceed.apply(authClient.idx, args);
    };
  }

  /**
   * Override getFooter to add "Forgot username?" link above "Back to login"
   */
  protected getFooter({ footerLink }: { footerLink: JQuery }) {
    const footers = [];

    // Only show "Forgot username?" link on password reset page, not on forgot username page
    const showForgotUsername = sessionStorage.getItem('showForgotUsernamePage') !== 'true';
    if (showForgotUsername) {
      footers.push(
        <div key="forgot-username">
          <ForgotUsernameLink onClick={() => this.toggleForgotUsernamePage()} />
        </div>
      );
    }

    // Add "Back to login" link (always show)
    if (footerLink.length) {
      footers.push(
        <div key="back-to-login">
          <BackToLogin oktaButton={footerLink} />
        </div>
      );
    }

    return <CommonFooter reactFooters={footers} />;
  }

  /**
   * Render only the forgot username page (hide all other elements)
   */
  protected async renderForgotUsernamePageOnly() {
    // Initialize renderReactComponent first
    await super.customize();

    // Hide all the Forge-rendered elements except footer
    setTimeout(() => {
      // Hide Forge header
      const forgeHeader = document.getElementById('athena-o-form-head');
      if (forgeHeader) {
        forgeHeader.classList.add('hide-element');
      }

      // Hide Forge input fields
      const forgeInputs = document.querySelectorAll('[id^="athena-"][id$="-input-field-container"]');
      forgeInputs.forEach((input) => {
        input.classList.add('hide-element');
      });

      // Hide Forge button bar
      const forgeButtonBar = document.getElementById('athena-o-form-button-bar');
      if (forgeButtonBar) {
        forgeButtonBar.classList.add('hide-element');
      }

      // Keep the footer visible - it will show "Back to login" button
      // The getFooter() method will hide the "Forgot username?" link automatically

      // Create wrapper for forgot username page
      const wrapper = createWrapper({ hidden: false });
      const formContent = document.querySelector('[data-se="o-form-content"]');
      if (formContent) {
        formContent.appendChild(wrapper);
      }

      // Render forgot username page
      this.renderReactComponent({
        id: 'forgot-username-page',
        component: (
          <ForgotUsernamePage
            onSuccess={(_email) => this.handleForgotUsernameSuccess()}
          />
        ),
        container: wrapper,
      });
    }, 0);
  }

  /**
   * Toggle between password reset page and forgot username page
   */
  protected toggleForgotUsernamePage() {
    // Check current state from sessionStorage
    const currentState = sessionStorage.getItem('showForgotUsernamePage') === 'true';
    const newState = !currentState;

    AmplitudeUtils.logCustomEvent('ForgotUsername::PageToggled', {
      showForgotUsernamePage: newState,
    });

    if (newState) {
      // Store flag to show forgot username page
      sessionStorage.setItem('showForgotUsernamePage', 'true');
    }
    else {
      // Clear flag to show password reset page
      sessionStorage.removeItem('showForgotUsernamePage');
    }

    // Trigger re-render by reloading the page
    // This is the simplest approach that works with OSW's architecture
    window.location.reload();
  }

  /**
   * Handle successful forgot username submission
   */
  protected handleForgotUsernameSuccess() {
    // Clear the forgot username page flag
    sessionStorage.removeItem('showForgotUsernamePage');

    // Store flag in sessionStorage to show success banner after page reload
    sessionStorage.setItem('showForgotUsernameSuccessBanner', 'true');

    // Reload to show password reset page with success banner
    window.location.reload();
  }

  /**
   * Show success banner after forgot username email is sent
   */
  protected showSuccessBanner() {
    // Show success banner
    setTimeout(() => {
      this.eventEmitter.emitEventToComponent({
        componentName: InfoContainer.displayName as string,
        eventName: 'set-info-banner',
        eventPayload: {
          headerText: 'Email Sent',
          description: 'Please check your email inbox for a message with your username enclosed. Enter your username below to reset your password.',
          alertType: 'success',
        },
      });
    }, 100);
  }
}
